import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaHeartbeat, FaArrowRight, FaLeaf, FaUsers, FaStar,
  FaCheckCircle, FaAppleAlt, FaDumbbell, FaBrain, FaMoon,
  FaHeart, FaShieldAlt, FaLightbulb, FaTrophy
} from 'react-icons/fa';
import images from '../assets/images';
import SEO, { PageSEO } from '../components/SEO';

const AboutUs = () => {
  const values = [
    {
      icon: FaLeaf,
      title: 'Natural & Sustainable',
      description: 'We believe real transformation happens through lifestyle change — not shortcuts. Every plan we create is built to last a lifetime.',
      color: 'text-success-600',
      bg: 'bg-success-100',
    },
    {
      icon: FaHeart,
      title: 'People-First',
      description: 'We treat every member as an individual. Your culture, preferences, schedule, and challenges shape your program — not the other way around.',
      color: 'text-red-500',
      bg: 'bg-red-100',
    },
    {
      icon: FaShieldAlt,
      title: 'Science-Backed',
      description: 'Every recommendation we make is grounded in nutritional science, exercise physiology, and behavioral psychology — not fad diets or trending content.',
      color: 'text-primary-600',
      bg: 'bg-primary-100',
    },
    {
      icon: FaLightbulb,
      title: 'Education First',
      description: 'We don\'t just tell you what to eat — we help you understand why. An informed member is an empowered one who never needs to come back to square one.',
      color: 'text-amber-500',
      bg: 'bg-amber-100',
    },
  ];

  const pillars = [
    { icon: FaAppleAlt, label: 'Nutrition', color: 'text-green-600', bg: 'bg-green-100' },
    { icon: FaDumbbell, label: 'Fitness', color: 'text-blue-600', bg: 'bg-blue-100' },
    { icon: FaUsers, label: 'Coaching', color: 'text-purple-600', bg: 'bg-purple-100' },
    { icon: FaMoon, label: 'Sleep', color: 'text-indigo-600', bg: 'bg-indigo-100' },
    { icon: FaBrain, label: 'Mindset', color: 'text-pink-600', bg: 'bg-pink-100' },
    { icon: FaHeartbeat, label: 'Lifestyle', color: 'text-red-500', bg: 'bg-red-100' },
  ];

  const team = [
    {
      name: 'Dr. Priya Sharma',
      role: 'Head of Nutrition Science',
      bio: 'MSc Nutrition & Dietetics, 10+ years helping Indians build sustainable eating habits through evidence-based guidance.',
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=300&h=300&fit=crop',
    },
    {
      name: 'Rahul Mehta',
      role: 'Lead Fitness Coach',
      bio: 'Certified Strength & Conditioning Specialist. Designed workout programs for 500+ clients from absolute beginners to competitive athletes.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop',
    },
    {
      name: 'Anita Kapoor',
      role: 'Behavioural Wellness Coach',
      bio: 'Certified Health & Wellness Coach specializing in habit psychology, emotional eating, and sustainable lifestyle change.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...PageSEO.about} />

      {/* Hero Section */}
      <section className="relative h-[calc(100vh-64px)] flex items-center overflow-hidden bg-gray-950">
        <div className="absolute inset-0 z-0">
          <img
            src={images.hero.about}
            alt="About Fitnession"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/90 via-gray-950/70 to-gray-950/50"></div>
        </div>

        <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-success-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-48 h-48 bg-primary-400/20 rounded-full blur-2xl"></div>
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center space-x-2 bg-white/15 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <FaLeaf className="text-success-400 text-sm" />
              <span className="text-sm font-medium text-white">India's Holistic Health Platform</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight drop-shadow-lg">
              We Help You Lose Weight
              <span className="block text-success-400 drop-shadow-lg">The Right Way —</span>
              <span className="block">Naturally & Permanently</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-100 mb-8 leading-relaxed max-w-xl drop-shadow-md">
              Fitnession was built on a simple belief: every person deserves a health program that fits their life — their food, their schedule, their body. No one-size-fits-all. No shortcuts. Just real results that last.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/our-program"
                className="inline-flex items-center justify-center bg-success-500 text-white px-7 py-3.5 rounded-xl hover:bg-success-600 transition-all text-base font-semibold shadow-2xl hover:scale-105 group"
              >
                Our Approach
                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/success-stories"
                className="inline-flex items-center justify-center bg-white/15 backdrop-blur-md border border-white/30 text-white px-7 py-3.5 rounded-xl hover:bg-white/25 transition-all text-base font-semibold hover:scale-105"
              >
                <FaTrophy className="mr-2" />
                See Member Results
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-20">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 0L60 8C120 16 240 32 360 37.3C480 43 600 37 720 34.7C840 32 960 32 1080 37.3C1200 43 1320 53 1380 58.7L1440 64V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0V0Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-success-100 rounded-2xl mb-8">
            <FaLeaf className="text-4xl text-success-600" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Mission</h2>
          <p className="text-2xl md:text-3xl text-gray-600 leading-relaxed font-light">
            To make <span className="text-primary-600 font-semibold">personalised, holistic health coaching</span> accessible
            to every Indian — combining smart nutrition, guided fitness, and dedicated 1:1 support
            to create <span className="text-success-600 font-semibold">permanent, natural transformations</span>.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-gradient-to-r from-primary-600 to-primary-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { number: '1,000+', label: 'Members Transformed' },
              { number: '18%', label: 'Average Weight Loss' },
              { number: '94%', label: 'Member Satisfaction' },
              { number: '85%', label: 'Maintain Results' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-4xl font-bold mb-1">{s.number}</div>
                <div className="text-primary-200 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-success-600 rounded-3xl transform rotate-3"></div>
              <img
                src={images.features.coaching}
                alt="Fitnession Coaching"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider">Our Story</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-6">
                Why We Built Fitnession
              </h2>
              <div className="space-y-5 text-lg text-gray-600 leading-relaxed">
                <p>
                  We watched too many people fail with generic diet plans that ignored their culture, family life, budget, and busy schedules. <span className="font-semibold text-gray-900">The problem was never willpower</span> — it was that the programs never really fit their lives.
                </p>
                <p>
                  Fitnession was built to change that. We combine <span className="font-semibold text-gray-900">personalised nutrition science, tailored fitness programs, and dedicated human coaching</span> — addressing not just what you eat, but how you sleep, manage stress, and build lasting habits.
                </p>
                <p>
                  Our members don't just lose weight. They gain energy, confidence, and a relationship with their body they never thought possible — <span className="font-semibold text-gray-900">naturally and permanently</span>.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-xl shadow-sm text-center">
                  <div className="text-3xl font-bold text-red-500">95%</div>
                  <div className="text-sm text-gray-600 mt-1">Fad diets fail within 5 years</div>
                </div>
                <div className="bg-white p-5 rounded-xl shadow-sm text-center">
                  <div className="text-3xl font-bold text-success-600">85%</div>
                  <div className="text-sm text-gray-600 mt-1">Our members keep the weight off</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach — Six Pillars */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Holistic Approach</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We don't just count calories. We address every dimension of your health so results actually stick.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {pillars.map((p) => (
              <div key={p.label} className="flex flex-col items-center text-center group">
                <div className={`w-16 h-16 ${p.bg} rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <p.icon className={`text-2xl ${p.color}`} />
                </div>
                <span className="font-semibold text-gray-800 text-sm">{p.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Personalised, not generic',
                desc: 'Your meal plan, workout routine, and coaching style are built specifically for you — your food preferences, your schedule, your body.',
              },
              {
                title: 'Habit-based, not willpower-based',
                desc: 'We engineer small, sustainable daily habits that compound over time. You won\'t need willpower because the right choices will become second nature.',
              },
              {
                title: 'Education at every step',
                desc: 'We explain the "why" behind every recommendation so you stay empowered and informed, even when your program evolves.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <FaCheckCircle className="text-success-500 text-xl mb-3" />
                <h3 className="font-bold text-gray-900 text-lg mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What We Stand For</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              These aren't just words on a wall — they're the principles behind every decision we make.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all group">
                <div className={`w-14 h-14 ${v.bg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <v.icon className={`text-2xl ${v.color}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{v.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      {/* <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Expert Team</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Certified professionals who are passionate about helping you achieve lasting health — without shortcuts.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {team.map((member) => (
              <div key={member.name} className="text-center group">
                <div className="relative inline-block mb-5">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-success-400 rounded-full transform scale-105 opacity-20 group-hover:opacity-40 transition-opacity"></div>
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-32 h-32 rounded-full object-cover mx-auto relative shadow-lg"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                <p className="text-primary-600 font-medium text-sm mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">{member.bio}</p>
                <div className="flex justify-center mt-3">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-amber-400 text-sm" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Why Choose Us — Checklist */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-success-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Thousands Trust Fitnession</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              '100% personalised — no two plans are the same',
              'Indian cuisine-friendly meal plans',
              'Dedicated 1:1 coach, not a chatbot',
              'Holistic — nutrition, fitness, sleep, and mindset',
              'Science-backed — no fad diets or gimmicks',
              'Flexible workouts — home or gym, your choice',
              // '30-day money-back guarantee',
              // 'Cancel anytime — no lock-in contracts',
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 shadow-sm">
                <FaCheckCircle className="text-success-500 text-lg flex-shrink-0" />
                <span className="text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Make the Change?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join our community of 1,000+ members who have transformed their bodies and their lives — naturally, permanently, and on their own terms.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center bg-success-500 text-white px-8 py-4 rounded-lg hover:bg-success-600 transition-all text-lg font-semibold shadow-2xl hover:shadow-success-500/50"
            >
              Start Your Journey
              <FaArrowRight className="ml-2" />
            </Link>
            <Link
              to="/success-stories"
              className="inline-flex items-center justify-center border-2 border-white text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
            >
              Read Success Stories
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
